import React, { useState, useEffect, useRef } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTeamsStore, type User } from "@/store/teamsStore";
import { cn } from "@/lib/utils";
import { formatUserName } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface UserDropdownProps {
  userId: string | null;
  onChangeUserId: (userId: string | null) => void;
  onChangeUser?: (user: User | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Custom fuzzy filter for Command component
const createFuzzyFilter = () => {
  return (value: string, search: string) => {
    if (!search) {
      return 1;
    } // Show all items when no search

    const normalizeText = (text: string) =>
      text
        .toLowerCase()
        .replace(/[-\s]+/g, " ")
        .trim();

    const normalizedValue = normalizeText(value);
    const normalizedSearch = normalizeText(search);

    // Exact match (highest score)
    if (normalizedValue === normalizedSearch) {
      return 1;
    }

    // Contains exact search phrase
    if (normalizedValue.includes(normalizedSearch)) {
      return 0.9;
    }

    // All search words are present (any order)
    const searchWords = normalizedSearch
      .split(" ")
      .filter((word) => word.length > 0);
    const valueWords = normalizedValue.split(" ");

    const matchedWords = searchWords.filter((searchWord) =>
      valueWords.some((valueWord) => valueWord.includes(searchWord)),
    );

    if (matchedWords.length === searchWords.length) {
      return 0.8; // All words found
    }

    if (matchedWords.length > 0) {
      return 0.3 + (matchedWords.length / searchWords.length) * 0.4; // Partial match
    }

    return 0; // No match
  };
};

export const UserDropdown: React.FC<UserDropdownProps> = ({
  userId,
  onChangeUserId,
  onChangeUser,
  placeholder = "Select a user...",
  className,
  disabled,
}) => {
  const { users, fetchUsers, loading } = useTeamsStore();
  const [open, setOpen] = useState(false);
  const commandListRef = useRef<HTMLDivElement>(null);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Add "None" option at the beginning
  const allOptions = [
    { id: null, displayName: "None", searchValue: "none" },
    ...users.map((user) => ({
      id: user.id,
      displayName: formatUserName(user.firstName, user.lastName, user.email),
      searchValue:
        `${formatUserName(user.firstName, user.lastName, user.email)} ${user.email}`.toLowerCase(),
    })),
  ];

  // Create fuzzy filter instance
  const fuzzyFilter = createFuzzyFilter();

  // Handler to scroll to top on search
  const handleSearchChange = () => {
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0;
    }
  };

  // Find the selected user
  const selectedOption = allOptions.find((option) => option.id === userId);

  // Handle user selection
  const handleSelect = (selectedUserId: string | null) => {
    const selectedUser = selectedUserId
      ? users.find((u) => u.id === selectedUserId)
      : null;
    onChangeUserId(selectedUserId);
    onChangeUser?.(selectedUser || null);
    setOpen(false);
  };

  return (
    <div className={cn("text-xs min-w-0", className)}>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between text-xs h-8 font-normal w-full"
            disabled={disabled || loading}
          >
            <span className="truncate">
              {selectedOption?.displayName || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command
            defaultValue={selectedOption?.searchValue || ""}
            filter={fuzzyFilter}
          >
            <CommandInput
              placeholder="Search users..."
              className="h-8"
              onValueChange={handleSearchChange}
            />
            <CommandList
              ref={commandListRef}
              className="max-h-[200px] overflow-y-auto"
            >
              <CommandEmpty>No user found.</CommandEmpty>
              <CommandGroup>
                {allOptions.map((option) => (
                  <CommandItem
                    key={option.id || "none"}
                    value={option.searchValue}
                    onSelect={() => handleSelect(option.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3 w-3",
                        userId === option.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span>{option.displayName}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
